import React, {
    useCallback,
    useLayoutEffect,
    useRef,
    useState
} from "react";
import { Button, Spin } from "antd";

import Icons from "components/layout/icons";
import Localize from "components/service/localize";
import {
    ATTENDANCE_ACTIONS,
    useAttendanceFeedbackAudio,
    useAttendanceSession
} from "components/attendance/hooks";
import AttendanceScannerModal from "components/attendance/modals/attendance-scanner-modal";


const COMPACT_STATUS_CLASS = "is-compact";

const AttendanceStatusBlock = ({ eeId, moment }) => {
    const statusCopyRef = useRef(null);
    const [ scannerVisible, setScannerVisible ] = useState(false);
    const [ scannerAction, setScannerAction ] = useState(ATTENDANCE_ACTIONS.CHECK_IN);
    const audioFeedback = useAttendanceFeedbackAudio();
    const {
        currentSession,
        isCheckedIn,
        loading,
        error,
        refetch
    } = useAttendanceSession(eeId);

    const action = isCheckedIn ?
        ATTENDANCE_ACTIONS.CHECK_OUT :
        ATTENDANCE_ACTIONS.CHECK_IN;

    const closeScanner = useCallback(() => {
        setScannerVisible(false);
    }, []);

    const openScanner = useCallback(() => {
        audioFeedback.unlockAudio();
        setScannerAction(action);
        setScannerVisible(true);
    }, [
        action,
        audioFeedback
    ]);

    const handleSuccess = useCallback(() => {
        refetch();
    }, [ refetch ]);

    const checkInTime = currentSession?.check_in_at && moment ?
        moment(currentSession.check_in_at).format("HH:mm") :
        "";

    useLayoutEffect(() => {
        const statusCopy = statusCopyRef.current;

        if (!statusCopy) {
            return undefined;
        }

        const fitStatusText = () => {
            statusCopy.classList.remove(COMPACT_STATUS_CLASS);

            if (!isCheckedIn || !statusCopy.clientHeight) {
                return;
            }

            const statusText = statusCopy.querySelector("strong");

            if (!statusText) {
                return;
            }

            const textStyle = window.getComputedStyle(statusText);
            const lineHeight = parseFloat(textStyle.lineHeight) || 16;
            const textHeight = statusText.getBoundingClientRect().height;

            if (textHeight > (lineHeight * 2) + 1) {
                statusCopy.classList.add(COMPACT_STATUS_CLASS);
            }
        };

        fitStatusText();

        const resizeObserver = window.ResizeObserver ?
            new window.ResizeObserver(fitStatusText) :
            null;
        const mutationObserver = window.MutationObserver ?
            new window.MutationObserver(fitStatusText) :
            null;

        resizeObserver?.observe(statusCopy);
        mutationObserver?.observe(statusCopy, {
            childList: true,
            characterData: true,
            subtree: true
        });
        window.addEventListener("resize", fitStatusText);

        return () => {
            resizeObserver?.disconnect();
            mutationObserver?.disconnect();
            window.removeEventListener("resize", fitStatusText);
            statusCopy.classList.remove(COMPACT_STATUS_CLASS);
        };
    }, [
        checkInTime,
        isCheckedIn
    ]);

    return (
        <>
            <section
                className={ `attendance-status-block${ isCheckedIn ? " is-checked-in" : "" }` }
                aria-live="polite"
            >
                <div ref={ statusCopyRef } className="attendance-status-copy">
                    {loading ? (
                        <Spin size="small" />
                    ) : error ? (
                        <span className="attendance-status-error">
                            <Localize>ATTENDANCE.Error_StatusLoad_Text</Localize>
                        </span>
                    ) : isCheckedIn ? (
                        <>
                            <span className="attendance-status-check">
                                <Icons.AttendanceSuccess />
                            </span>
                            <strong>
                                <Localize time={ checkInTime } br={ <br /> }>
                                    ATTENDANCE.Result_CheckedInAt
                                </Localize>
                            </strong>
                        </>
                    ) : (
                        <>
                            <span className="attendance-status-label">
                                <Localize>ATTENDANCE.Overview_NotCheckedIn</Localize>
                            </span>
                            <strong>
                                <Localize br={ <br /> }>
                                    ATTENDANCE.Overview_CheckInPrompt
                                </Localize>
                            </strong>
                        </>
                    )}
                </div>
                <Button
                    type="primary"
                    className="attendance-status-action"
                    disabled={ loading || Boolean(error) || !eeId }
                    onClick={ openScanner }
                >
                    <Icons.QrCode />
                    <Localize>
                        {isCheckedIn ?
                            "ATTENDANCE.Button_CheckOut" :
                            "ATTENDANCE.Button_CheckIn"}
                    </Localize>
                </Button>
            </section>
            <AttendanceScannerModal
                visible={ scannerVisible }
                action={ scannerAction }
                eeId={ eeId }
                moment={ moment }
                audioFeedback={ audioFeedback }
                onSuccess={ handleSuccess }
                onClose={ closeScanner }
            />
        </>
    );
};

export default AttendanceStatusBlock;
